/**
 * 聊天气泡 (报价 / 画图 / 问答记录共用):
 *   用户  = 品牌绿实心气泡, 白字, 靠右
 *   助手  = 白色卡片 (边框 + shadow-sm), 左侧圆形助手头像
 * ChatRow 只负责一行的排布 (头像 + 内容列), 报价单 / 图片结果卡片作为 children 挂在气泡下面。
 */
import type { ReactNode } from "react";
import { AlertCircle, Bot } from "lucide-react";
import { cn } from "@/lib/utils";

type Role = "user" | "assistant";

export function AssistantAvatar({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        "flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand to-primary text-white shadow-sm",
        className,
      )}
    >
      <Bot className="h-[18px] w-[18px]" strokeWidth={2} />
    </span>
  );
}

/** 一行消息: 用户靠右; 助手 = 头像 + 内容列 */
export function ChatRow({ role, children, className }: { role: Role; children: ReactNode; className?: string }) {
  if (role === "user") {
    return <div className={cn("flex w-full flex-col items-end gap-2 pl-10 md:pl-16", className)}>{children}</div>;
  }
  return (
    <div className={cn("flex w-full gap-2.5 pr-2 md:gap-3 md:pr-16", className)}>
      <AssistantAvatar className="mt-0.5" />
      <div className="flex min-w-0 flex-1 flex-col items-start gap-2">{children}</div>
    </div>
  );
}

const bubbleBase = "max-w-full whitespace-pre-wrap break-words rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed md:px-4";

export function bubbleClass(role: Role, tone: "default" | "danger" = "default") {
  if (role === "user") return cn(bubbleBase, "rounded-br-md bg-gradient-to-br from-brand to-primary text-primary-foreground shadow-sm");
  if (tone === "danger") return cn(bubbleBase, "rounded-tl-md border border-danger/25 bg-danger-soft text-danger-strong");
  return cn(bubbleBase, "rounded-tl-md border bg-card text-card-foreground shadow-[0_2px_4px_rgba(0,0,0,0.08)]");
}

interface Props {
  role: Role;
  content: ReactNode;
  /** 乐观更新中的气泡 (半透明) */
  pending?: boolean;
  /** danger: 错误提示样式 (带图标) */
  tone?: "default" | "danger";
  /** 挂在气泡下方的内容 (报价单等) */
  children?: ReactNode;
  className?: string;
}

export function ChatBubble({ role, content, pending, tone = "default", children, className }: Props) {
  return (
    <ChatRow role={role} className={className}>
      <div className={cn(bubbleClass(role, tone), pending && "opacity-70")}>
        {tone === "danger" ? (
          <span className="flex items-start gap-2">
            <AlertCircle aria-hidden className="mt-[3px] h-4 w-4 shrink-0" />
            <span className="min-w-0">{content}</span>
          </span>
        ) : (
          content
        )}
      </div>
      {children}
    </ChatRow>
  );
}

/** 助手「正在输入」: 三个跳动的点 */
export function TypingBubble({ label = "正在思考" }: { label?: string }) {
  return (
    <ChatRow role="assistant">
      <div className={cn(bubbleClass("assistant"), "flex items-center gap-1 py-3.5")} role="status" aria-label={label}>
        {[0, 150, 300].map((d) => (
          <span
            key={d}
            aria-hidden
            className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/60"
            style={{ animationDelay: `${d}ms` }}
          />
        ))}
      </div>
    </ChatRow>
  );
}
